import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useSnack } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';

import {
  AllRegistrationScreenParams,
  getRegistrationScreenPath,
  RegistrationCommonStep
} from '../../../../../utils/navigation';
import { updateRegistration as updateRegistrationAction } from '../../../../../store/registration/actions';
import { getDetailingSteps } from '../../../../../store/registration/selectors';
import { getSteps } from '../../../../../screens/registration/useRegistrationForm/utils';
import { useUser } from '../../../../../hooks/useUser';
import { useUpdateRegistration } from '../../../../../screens/registration/useRegistrationForm/useUpdateRegistration';
import { createErrorMessage } from '../../../../../utils/utils';
import { useCreateRegistration } from '../../../../../screens/registration/useRegistrationForm/useCreateRegistration';
import { Energimerkeversjon } from '../../../../../types/energimerke';

export const useRegistrationSubmit = () => {
  const dispatch = useDispatch();
  const { addSnack } = useSnack();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useStore();
  const detailingSteps = useSelector(getDetailingSteps, _.isEqual);
  const { user } = useUser();

  const { buildingId, registrationId } = useParams<AllRegistrationScreenParams>();

  const { createRegistration, createIsLoading } = useCreateRegistration(buildingId);

  const { updateRegistration, updateIsLoading } = useUpdateRegistration();

  const steps = getSteps(user, detailingSteps);

  const onCreateRegistration = (energimerkeversjon: Energimerkeversjon) => {
    createRegistration(
      { energimerkeversjon },
      {
        onSuccess: (data) => {
          // Update redux state with data from created registration
          dispatch(updateRegistrationAction({ userInput: data.userInput }));
          addSnack(t('registerScreen.saveRegistration.saveSucceeded'));

          // On success new single registration
          const path = getRegistrationScreenPath({
            buildingId: data.energiObjektId,
            registrationId: data.registreringId,
            step: steps[steps.indexOf(RegistrationCommonStep.ABOUT) + 1]
          });

          navigate(path);
        }
      }
    );
  };

  const onUpdateRegistration = () => {
    const userInput = store.getState().register.userInput;
    const nextStep = steps[steps.indexOf(RegistrationCommonStep.ABOUT) + 1];

    updateRegistration(
      { registrationId, userInput },
      {
        onSuccess: () => navigate(`../../${nextStep}`),
        onError: () => {
          const errorMessage = createErrorMessage(t('registerScreen.saveRegistration.saveFailed'));

          addSnack(errorMessage, { variant: 'error' });
        }
      }
    );
  };

  const onSubmit = (energimerkeversjon: Energimerkeversjon) => {
    if (!registrationId) {
      onCreateRegistration(energimerkeversjon);
      return;
    }
    onUpdateRegistration();
  };

  return {
    onRegistrationSubmit: onSubmit,
    isLoading: createIsLoading || updateIsLoading
  };
};
